import { useRef, useState } from 'react';
import { X, Upload, FileText, Loader2, AlertCircle, AlertTriangle, CheckCircle } from 'lucide-react';
import toast from 'react-hot-toast';
import { storageService } from '@/services/storage.service';
import { courseService } from '@/services/course.service';
import { UploadProgress } from '@/types/storage.types';

interface UploadContentFileModalProps {
  isOpen: boolean;
  contentItemId: string;
  contentItemTitle?: string;
  /** True when the item already has authored HTML, which the upload will replace. */
  hasExistingContent?: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

const ACCEPT = '.pdf';
const ALLOWED_EXT = ['pdf'];
// Matches the server gate in TenantBoundaryEnforcer, which rejects anything over
// 50 MB on /storage/utils/upload - promising more here just fails later.
const MAX_SIZE_MB = 50;
const MAX_SIZE_BYTES = MAX_SIZE_MB * 1024 * 1024;

/**
 * Upload a single PDF file into a content item. The raw file goes to S3 via the
 * storage service; the backend then embeds it in a themed HTML page, stores that
 * HTML in S3 and marks the item read-only. PDF only, max 50 MB.
 *
 * Allowed on an item that already has authored HTML — the upload replaces it, so
 * that case is called out before the user commits.
 */
export default function UploadContentFileModal({
  isOpen,
  contentItemId,
  contentItemTitle,
  hasExistingContent = false,
  onClose,
  onSuccess,
}: UploadContentFileModalProps) {
  const [file, setFile] = useState<File | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const pickFile = (f: File | undefined | null) => {
    if (!f) return;
    const ext = f.name.split('.').pop()?.toLowerCase() || '';
    if (!ALLOWED_EXT.includes(ext)) {
      toast.error('Only PDF files are allowed');
      return;
    }
    if (f.size > MAX_SIZE_BYTES) {
      toast.error(`File is too large. Maximum size is ${MAX_SIZE_MB} MB.`);
      return;
    }
    setFile(f);
  };

  const handleClose = () => {
    if (isUploading) {
      toast.error('Please wait for the upload to complete');
      return;
    }
    setFile(null);
    setProgress(0);
    onClose();
  };

  const handleUpload = async () => {
    if (!file) {
      toast.error('Please select a file');
      return;
    }
    // Defensive re-check so an oversized file can never reach S3.
    if (file.size > MAX_SIZE_BYTES) {
      toast.error(`File is too large. Maximum size is ${MAX_SIZE_MB} MB.`);
      return;
    }
    setIsUploading(true);
    setProgress(0);
    const loading = toast.loading('Uploading file...');
    try {
      // 1) Upload the raw file to S3.
      const uploaded = await storageService.uploadFile(file, {
        onProgress: (p: UploadProgress) => setProgress(p.percentage),
      });
      const fileUrl = uploaded.url || (uploaded as any).file_path || '';
      const fileKey = uploaded.key || (uploaded as any).file_path || '';
      const fileType = uploaded.fileType || file.type || '';

      // 2) Ask the backend to attach it to the content item.
      await courseService.uploadContentItemDocument(contentItemId, {
        file_url: fileUrl,
        file_key: fileKey,
        file_type: fileType,
        file_name: uploaded.name || file.name,
        file_size: file.size,
      });

      toast.success('File uploaded and added to the content item', { id: loading });
      setFile(null);
      setProgress(0);
      onSuccess();
      onClose();
    } catch (error: any) {
      toast.error(error?.message || 'Failed to upload the file', { id: loading });
    } finally {
      setIsUploading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[60] p-4" dir="ltr">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-lg flex flex-col">
        {/* Header */}
        <div className="p-5 border-b flex items-center justify-between">
          <div>
            <h2 className="text-lg font-semibold text-gray-900">Upload a Document</h2>
            <p className="text-sm text-gray-500 mt-0.5">
              {contentItemTitle ? `Into "${contentItemTitle}"` : 'PDF only — shown here as-is'}
            </p>
          </div>
          <button onClick={handleClose} disabled={isUploading} className="p-1 rounded-full hover:bg-gray-100 disabled:opacity-50">
            <X size={22} />
          </button>
        </div>

        {/* Body */}
        <div className="p-5">
          {hasExistingContent && (
            <div className="mb-4 flex items-start gap-2.5 px-3 py-2.5 bg-amber-50 border border-amber-200 rounded-lg">
              <AlertTriangle className="w-4 h-4 text-amber-600 flex-shrink-0 mt-0.5" />
              <p className="text-xs text-amber-900">
                This item already has content. Uploading a file <span className="font-semibold">replaces it</span>,
                and the item becomes read-only. You can remove the file later to get the editor back, but the
                current content will not come back with it.
              </p>
            </div>
          )}

          {!file ? (
            <div
              onDrop={(e) => { e.preventDefault(); pickFile(e.dataTransfer.files?.[0]); }}
              onDragOver={(e) => e.preventDefault()}
              className="border-2 border-dashed border-gray-300 rounded-xl p-8 text-center hover:border-brand-indigo-400 transition-colors"
            >
              <Upload className="w-10 h-10 text-gray-400 mx-auto mb-3" />
              <p className="text-sm font-medium text-gray-800 mb-1">Drop a file here or click to browse</p>
              <p className="text-xs text-gray-500 mb-4">PDF only · max {MAX_SIZE_MB} MB</p>
              <input
                ref={fileInputRef}
                type="file"
                accept={ACCEPT}
                className="hidden"
                onChange={(e) => pickFile(e.target.files?.[0])}
              />
              <button onClick={() => fileInputRef.current?.click()} className="btn btn-primary">
                Select File
              </button>
            </div>
          ) : (
            <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
              <FileText className="w-5 h-5 text-gray-500 flex-shrink-0" />
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-gray-900 truncate">{file.name}</p>
                <p className="text-xs text-gray-500">{storageService.formatFileSize(file.size)}</p>
                {isUploading && (
                  <div className="bg-gray-200 rounded-full h-1.5 mt-2">
                    <div className="bg-brand-indigo-500 h-1.5 rounded-full transition-all" style={{ width: `${progress}%` }} />
                  </div>
                )}
              </div>
              {isUploading ? (
                <Loader2 className="w-5 h-5 text-brand-indigo-500 animate-spin" />
              ) : progress === 100 ? (
                <CheckCircle className="w-5 h-5 text-green-500" />
              ) : (
                <button onClick={() => setFile(null)} className="text-gray-400 hover:text-red-600">
                  <X className="w-4 h-4" />
                </button>
              )}
            </div>
          )}

          <div className="flex items-start gap-2 mt-4 text-xs text-gray-500">
            <AlertCircle className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" />
            <span>Uploaded files are shown as-is and can't be edited in the HTML editor — only deleted.</span>
          </div>
        </div>

        {/* Footer */}
        <div className="p-4 bg-gray-50 rounded-b-xl flex justify-end gap-3">
          <button onClick={handleClose} disabled={isUploading} className="btn btn-secondary">
            Cancel
          </button>
          <button onClick={handleUpload} disabled={!file || isUploading} className="btn btn-primary inline-flex items-center gap-2">
            {isUploading ? (<><Loader2 className="w-4 h-4 animate-spin" /> Uploading…</>) : (<><Upload className="w-4 h-4" /> Upload</>)}
          </button>
        </div>
      </div>
    </div>
  );
}
